import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { VehicleCard } from "@/components/vehicle/VehicleCard";
import { VehicleCardSkeleton } from "@/components/vehicle/VehicleCardSkeleton";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Filter, X } from "lucide-react";

export default function BrowsePage() {
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "all";
  const sort = params.get("sort") || "popular";
  const maxPrice = Number(params.get("maxPrice") || 6000);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams();
        if (category !== "all") q.set("category", category);
        q.set("max_monthly", String(maxPrice));
        q.set("limit", "100");
        const res = await axios.get(`${API}/vehicles?${q.toString()}`);
        setVehicles(res.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, maxPrice]);

  const sorted = useMemo(() => {
    const list = [...vehicles];
    if (sort === "price_asc") list.sort((a, b) => (a.price_weekly || 0) - (b.price_weekly || 0));
    else if (sort === "price_desc") list.sort((a, b) => (b.price_weekly || 0) - (a.price_weekly || 0));
    else list.sort((a, b) => (b.booked_this_month || 0) - (a.booked_this_month || 0));
    return list;
  }, [vehicles, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === null || v === undefined || v === "" || v === "all") next.delete(k);
    else next.set(k, String(v));
    setParams(next, { replace: true });
  };

  return (
    <div className="bg-background" data-testid="browse-page">
      <div className="border-b border-border light-wash">
        <div className="container-x py-10 sm:py-14">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Browse the fleet</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-1">Find a vehicle for every drive</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Sedans, SUVs, electric vehicles, trucks, and business-ready cars. Filter by category and daily price. Weekly, monthly, and Rental Term Leasing plans available.
          </p>
        </div>
      </div>

      <div className="container-x py-6" data-testid="browse-filters">
        <div className="flex flex-wrap items-center gap-3">
          <div className="hidden md:flex items-center gap-2 flex-wrap">
            <button
              className={`chip ${category === "all" ? "chip-active" : ""}`}
              onClick={() => setParam("category", "all")}
              data-testid="vehicle-filter-chip-all"
            >
              All
            </button>
            {CATEGORY_ORDER.map((k) => (
              <button
                key={k}
                className={`chip ${category === k ? "chip-active" : ""}`}
                onClick={() => setParam("category", k)}
                data-testid={`vehicle-filter-chip-${k}`}
              >
                {CATEGORY_LABELS[k]}
              </button>
            ))}
          </div>

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full" data-testid="mobile-filters-trigger">
                  <Filter className="h-4 w-4 mr-2" /> Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[80vh]">
                <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                <div className="mt-5 space-y-6">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Category</div>
                    <div className="flex flex-wrap gap-2">
                      <button className={`chip ${category === "all" ? "chip-active" : ""}`} onClick={() => setParam("category", "all")}>All</button>
                      {CATEGORY_ORDER.map((k) => (
                        <button key={k} className={`chip ${category === k ? "chip-active" : ""}`} onClick={() => setParam("category", k)}>{CATEGORY_LABELS[k]}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Max monthly: ${maxPrice}</div>
                    <Slider defaultValue={[maxPrice]} min={500} max={15000} step={100} onValueCommit={(v) => setParam("maxPrice", v[0])} data-testid="mobile-filter-price" />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden md:flex items-center gap-3">
              <div className="w-56">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Max monthly: ${maxPrice}</div>
                <Slider defaultValue={[maxPrice]} min={500} max={15000} step={100} onValueCommit={(v) => setParam("maxPrice", v[0])} data-testid="filter-price" />
              </div>
            </div>
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="w-[190px] rounded-xl" data-testid="vehicle-sort-select">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">Most booked</SelectItem>
                <SelectItem value="price_asc">Price — low to high</SelectItem>
                <SelectItem value="price_desc">Price — high to low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vehicle-grid">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <VehicleCardSkeleton key={i} />)
            : sorted.length === 0
              ? (
                <div className="col-span-full text-center py-16">
                  <p className="text-muted-foreground">No vehicles match those filters.</p>
                  <Button variant="ghost" onClick={() => setParams({})} className="mt-3"><X className="h-4 w-4 mr-1" /> Reset filters</Button>
                </div>
              )
              : sorted.map((v) => <VehicleCard key={v.id} vehicle={v} />)
          }
        </div>

        <div className="mt-14 card-clean p-6 sm:p-8 text-center">
          <p className="text-sm text-muted-foreground">Looking for a business or fleet fit?</p>
          <div className="mt-3 flex justify-center gap-3 flex-wrap">
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/alfa-one-path">Explore Alfa One Path (business)</Link>
            </Button>
            <Button asChild className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]">
              <Link to="/drive-alfa-club">Join Drive Alfa Club</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
